/** Officials and staff directory (FR-OFF-01): terms, committees, signatories. Past terms are kept. */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PenLine, Plus } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { manilaToday } from '../../lib/queries';
import { Alert, Button, Card, EmptyState, Field, Modal, PageHeader, Skeleton, StatusChip, TableWrap, Td, Th, inputClass } from '../../components/ui';

export const OFFICIAL_KINDS: Record<string, string> = {
  PUNONG_BARANGAY: 'Punong Barangay',
  KAGAWAD: 'Barangay Kagawad',
  SK_CHAIRPERSON: 'SK Chairperson',
  SECRETARY: 'Barangay Secretary',
  TREASURER: 'Barangay Treasurer',
  APPOINTED_STAFF: 'Appointed staff',
  TANOD: 'Barangay Tanod',
  BHW: 'Barangay Health Worker',
  LUPON_MEMBER: 'Lupon member',
  SK_KAGAWAD: 'SK Kagawad',
  SK_SECRETARY: 'SK Secretary',
  SK_TREASURER: 'SK Treasurer',
  OTHER: 'Other',
};

export interface Official {
  id: string;
  kind: string;
  title: string;
  fullName: string;
  contact: string;
  email: string;
  committee: string;
  termStart: string;
  termEnd: string | null;
  canSign: boolean;
  sortOrder: number;
  current: boolean;
}

const EMPTY = { kind: 'KAGAWAD', title: '', fullName: '', contact: '', email: '', committee: '', termStart: manilaToday(), termEnd: '', canSign: false, sortOrder: 0 };

export function OfficialsPage(): JSX.Element {
  const { can } = useAuth();
  const editable = can('officials.manage');
  const qc = useQueryClient();
  const [showPast, setShowPast] = useState(false);
  const [editing, setEditing] = useState<(typeof EMPTY & { id?: string }) | null>(null);
  const list = useQuery({ queryKey: ['officials'], queryFn: () => api<Official[]>('/barangay/officials') });
  const save = useMutation({
    mutationFn: (o: typeof EMPTY & { id?: string }) => {
      const body = { ...o, termEnd: o.termEnd || null, id: undefined };
      return o.id ? api<Official>(`/barangay/officials/${o.id}`, { method: 'PATCH', body }) : api<Official>('/barangay/officials', { method: 'POST', body });
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['officials'] }); setEditing(null); },
  });
  const fieldError = (name: string) => (save.error instanceof ApiError ? save.error.fields[name] : undefined);
  const rows = (list.data ?? []).filter((o) => showPast || o.current);

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader
        title="Officials and staff"
        description="Elected officials, appointed staff, tanods, BHWs and Lupon members. Signatories on documents come from the current roster."
        actions={editable && <Button onClick={() => { save.reset(); setEditing({ ...EMPTY }); }}><Plus aria-hidden="true" className="h-4 w-4" /> Add</Button>}
      />
      <label className="mb-3 flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
        <input type="checkbox" className="h-4 w-4" checked={showPast} onChange={(e) => setShowPast(e.target.checked)} /> Show past terms
      </label>
      {list.isLoading ? <Skeleton className="h-64 w-full" /> : rows.length === 0 ? (
        <Card><EmptyState title="No officials yet" description="Start with the Punong Barangay and the Barangay Secretary; mark who may sign documents." /></Card>
      ) : (
        <Card>
          <TableWrap>
            <thead><tr><Th>Name</Th><Th>Post</Th><Th>Term</Th><Th>Contact</Th><Th>{''}</Th></tr></thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {rows.map((o) => (
                <tr key={o.id} className={o.current ? '' : 'opacity-60'}>
                  <Td>
                    <div className="font-medium">{o.fullName}</div>
                    <div className="flex flex-wrap gap-1 pt-0.5">
                      {o.canSign && <StatusChip tone="info">Signatory</StatusChip>}
                      {!o.current && <StatusChip tone="neutral">Past term</StatusChip>}
                    </div>
                  </Td>
                  <Td><div>{o.title}</div>{o.committee && <div className="text-xs text-slate-500">{o.committee}</div>}</Td>
                  <Td><span className="text-sm">{o.termStart} – {o.termEnd ?? 'present'}</span></Td>
                  <Td><span className="text-sm">{o.contact}</span>{o.email && <div className="text-xs text-slate-500">{o.email}</div>}</Td>
                  <Td>{editable && <button type="button" aria-label={`Edit ${o.fullName}`} className="grid h-9 w-9 place-items-center rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800" onClick={() => { save.reset(); setEditing({ ...EMPTY, ...o, termEnd: o.termEnd ?? '' }); }}><PenLine aria-hidden="true" className="h-4 w-4" /></button>}</Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        </Card>
      )}
      {editing && (
        <Modal title={editing.id ? `Edit ${editing.fullName}` : 'Add official or staff'} onClose={() => setEditing(null)}>
          <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); save.mutate(editing); }}>
            {save.error && !(save.error instanceof ApiError && Object.keys(save.error.fields).length) && <Alert tone="critical">{save.error.message}</Alert>}
            <Field label="Post" required>
              <select className={inputClass} value={editing.kind} onChange={(e) => setEditing({ ...editing, kind: e.target.value, canSign: e.target.value === 'PUNONG_BARANGAY' ? true : editing.canSign })}>
                {Object.entries(OFFICIAL_KINDS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
            <Field label="Full name" required error={fieldError('fullName')}><input className={inputClass} value={editing.fullName} maxLength={160} onChange={(e) => setEditing({ ...editing, fullName: e.target.value })} autoFocus /></Field>
            <Field label="Title as printed" hint="Blank = the post's name"><input className={inputClass} value={editing.title} maxLength={120} onChange={(e) => setEditing({ ...editing, title: e.target.value })} /></Field>
            <Field label="Committee" hint="For Kagawads, e.g. Committee on Peace and Order"><input className={inputClass} value={editing.committee} maxLength={160} onChange={(e) => setEditing({ ...editing, committee: e.target.value })} /></Field>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Term starts" required error={fieldError('termStart')}><input type="date" className={inputClass} value={editing.termStart} onChange={(e) => setEditing({ ...editing, termStart: e.target.value })} /></Field>
              <Field label="Term ends" error={fieldError('termEnd')}><input type="date" className={inputClass} value={editing.termEnd} onChange={(e) => setEditing({ ...editing, termEnd: e.target.value })} /></Field>
              <Field label="Contact number"><input className={inputClass} value={editing.contact} maxLength={60} onChange={(e) => setEditing({ ...editing, contact: e.target.value })} /></Field>
              <Field label="Email"><input className={inputClass} value={editing.email} maxLength={160} onChange={(e) => setEditing({ ...editing, email: e.target.value })} /></Field>
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="h-4 w-4" checked={editing.canSign} onChange={(e) => setEditing({ ...editing, canSign: e.target.checked })} /> May sign certificates and clearances</label>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
              <Button type="submit" busy={save.isPending}>Save</Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
